import { useForm } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'

import { useToast } from '@wartek-id/toast-v2'

import {
  API_GET_DIRECT_REPORTS,
  useApiPostAtasan,
} from 'api/kinerja/kepala-sekolah/atasan'
import { queryKeyWithNpsn } from 'api/kinerja/kepala-sekolah/config'

import type { TAtasan } from 'types/kinerja-atasan'
import type { School } from 'types/kinerja-ks'
import type { TFormValues } from 'app/KinerjaKSPage/components/CardDataConfirmation/utils/types'

type TContentType = 'FORM' | 'CONSENT' | 'CONSENT_DINAS' | 'CONSENT_RECONFIRM'

export const useConfirmationData = ({
  npsn,
  data,
  isReconfirm = false,
  onClose,
}: {
  npsn: School['npsn']
  data: TAtasan
  isReconfirm: boolean
  onClose?: () => void
}) => {
  const queryClient = useQueryClient()
  const toast = useToast()

  const [showContent, setShowContent] = useState<TContentType>(
    isReconfirm ? 'CONSENT_RECONFIRM' : 'FORM'
  )

  // Hold the state of the consent form
  const [isConsentAccepted, setIsConsentAccepted] = useState<boolean>(false)

  // Hold the state of the dinas consent form
  const [isConsentDinasAccepted, setIsConsentDinasAccepted] =
    useState<boolean>(false)

  // Hold the state of the dinas consent form
  const [isConcentReconfirmAccepted, setIsConsentReconfirmAccepted] =
    useState<boolean>(false)

  const { mutateAsync, isLoading: isSubmitting } = useApiPostAtasan()

  const methods = useForm<TFormValues>({
    mode: 'onChange',
    defaultValues: {
      pengawas: { label: null, value: null },
      dinas: { label: null, value: null },
    },
  })

  const {
    getValues,
    formState: { isValid },
    setValue,
    reset,
  } = methods

  const isInputValid = isValid
  const dataPengawas = getValues()?.pengawas
  const dataDinas = getValues()?.dinas
  const valuePengawas = dataPengawas?.value
  const valueDinas = dataDinas?.value
  const isProceedWithDinas =
    valuePengawas === 'REJECTED' && valueDinas === 'CONFIRMED'

  const submit = () => {
    mutateAsync(
      {
        npsn,
        pengawas: valuePengawas,
        dinas: valueDinas,
      },
      {
        onError: () => {
          toast({
            message: 'Gagal konfirmasi data atasan. Coba lagi.',
          })
        },
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: queryKeyWithNpsn(API_GET_DIRECT_REPORTS, npsn),
          })

          toast({
            message: 'Berhasil konfirmasi data atasan!',
            type: 'success',
          })

          close()
        },
      }
    )
  }

  const close = () => {
    reset()
    setShowContent(null)
    onClose?.()
  }

  useEffect(() => {
    setValue('pengawas.label', data?.pengawas?.nama || null)
    setValue('dinas.label', data?.dinas?.wilayah || null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    showContent,
    setShowContent,

    isConsentAccepted,
    setIsConsentAccepted,

    isConsentDinasAccepted,
    setIsConsentDinasAccepted,

    isConcentReconfirmAccepted,
    setIsConsentReconfirmAccepted,

    methods,

    isInputValid,
    dataPengawas,
    dataDinas,
    valuePengawas,
    valueDinas,
    isProceedWithDinas,

    isSubmitting,
    submit,
    close,
  }
}
